import { FC, useCallback, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import Button from 'react-bootstrap/Button'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import GoBackHeader from 'components/organisms/GoBackHeader'
import {
  setSelectedDocumentId,
  setSelectedDocument,
  setSelectedDocumentIndex
} from 'reducers/document/documentProcessingSlice'
import { isDebtorType1 } from 'reducers/activecase/activecaseSlice'
import { Document } from 'share/domain/document'
import {
  GET_DOCUMENTS_CHILD_LEAF_CATEGORY_QUERY,
  GetDocumentsChildLeafCategoryQueryResponse,
  GetDocumentsChildLeafCategoryQueryVariables
} from 'share/queries/documents'
import {
  CERTIFICATE_EXISTS_BY_CASE,
  CertificateExistsByCaseResponse,
  CertificateExistsByCaseVariables
} from 'share/queries/courses'
import ContentNotAvailable from 'components/atoms/ContentNotAvailable'

import DocumentsAddReplaceModal from '../DocumentsAddReplaceModal'
import {
  DocumentsFolderBtnContainer,
  DocumentsFolderCertificateContainer,
  DocumentsFolderContainer,
  DocumentListEmptyContainer,
  DocumentsFolderList,
  DocumentsFolderListItem,
  DocumentsFolderListItemContent,
  DocumentsFolderListItemIcon
} from './styles'

const CERTIFICATE_OF_CREDIT_COUNSELING = 'certificate of credit counseling'

function formatDate(
  dateStr: string | null | undefined
): string | null | undefined {
  const today = new Date()
  try {
    if (dateStr) {
      const dt = new Date(dateStr)
      if (
        today.getFullYear() === dt.getFullYear() &&
        today.getMonth() === dt.getMonth() &&
        today.getDate() === dt.getDate()
      ) {
        return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    }
    return dateStr
  } catch {
    return dateStr
  }
}

const getStatus = (status: string, withIcon = false): string => {
  switch (status.toLowerCase()) {
    case 'pending':
      return `${withIcon && 'mdi mdi-clock-outline'} yellow`
    case 'rejected':
      return `${withIcon && 'mdi mdi-alert-rhombus-outline'} red`
    case 'accepted':
      return `${withIcon && 'mdi mdi-check-circle-outline'} green`
    default:
      return ''
  }
}

interface IHistory {
  navFromCourses: boolean
  businessObjectId: number | null
  businessObjectType: string
}

const DocumentsChildFolder: FC = () => {
  const history = useHistory<IHistory>()
  const dispatch = useAppDispatch()
  const [documents, setDocuments] = useState<Document[]>([])
  const [goBackHeaderName, setGoBackHeaderName] = useState('')
  const [businessObjectId, setBusinessObjectId] = useState<number | null>(null)
  const [businessObjectType, setBusinessObjectType] = useState<string>('')
  const [certificateExists, setCertificateExists] = useState(false)
  const caseData = useAppSelector((state) => state.activeCase)
  const [showDocumentsAddReplaceModal, setShowDocumentsAddReplaceModal] =
    useState(false)
  const documentProcessing = useAppSelector((state) => state.documentProcessing)
  const isDebtor1 = useAppSelector(isDebtorType1)

  const { data, loading, error } = useQuery<
    GetDocumentsChildLeafCategoryQueryResponse,
    GetDocumentsChildLeafCategoryQueryVariables
  >(GET_DOCUMENTS_CHILD_LEAF_CATEGORY_QUERY, {
    variables: {
      categoryId: documentProcessing.selectedDocumentCategoryId
        ? documentProcessing.selectedDocumentCategoryId
        : 0,
      caseId: caseData.matter?.id || 0
    }
  })

  const {
    data: dataCertificate,
    loading: loadingCertificate,
    error: errorCertificate
  } = useQuery<
    CertificateExistsByCaseResponse,
    CertificateExistsByCaseVariables
  >(CERTIFICATE_EXISTS_BY_CASE, {
    variables: {
      caseId: caseData.matter?.id || 0
    }
  })

  function pendingOrAcceptedDocumentsCount(documents: Document[]): number {
    let count = 0
    for (let i = 0; i < documents.length; i++) {
      if (
        documents[i].status === 'Pending' ||
        documents[i].status === 'Accepted'
      ) {
        count = count + 1
      }
    }
    return count
  }

  function getCertificateUploadObjectType(): string {
    let businessObjectType = 'order-first'
    if (isDebtor1) {
      businessObjectType += '-debtor1'
    } else {
      businessObjectType += '-debtor2'
    }
    return businessObjectType
  }

  const isCertificate =
    documentProcessing?.selectedDocumentCategory?.name.toLowerCase() ===
    CERTIFICATE_OF_CREDIT_COUNSELING

  useEffect(() => {
    // check to see if we were routed to here from the courses page
    if (history?.location?.state?.navFromCourses) {
      setGoBackHeaderName('General Documents')
      setBusinessObjectId(history?.location?.state?.businessObjectId)
      setBusinessObjectType(history?.location?.state?.businessObjectType)
    } else {
      setGoBackHeaderName(
        documentProcessing.selectedDocumentCategory?.name
          ? documentProcessing.selectedDocumentCategory?.name
          : ''
      )
      if (isCertificate) {
        const businessObjType = getCertificateUploadObjectType()
        setBusinessObjectType(businessObjType)
      }
    }
  }, [])

  useEffect(() => {
    if (
      data &&
      data?.documents.documentsChildLeafCategory.documentList.length > 0
    ) {
      setDocuments(data?.documents.documentsChildLeafCategory.documentList)

      console.log(
        'categories',
        data?.documents.documentsChildLeafCategory.documentList
      )
    }
  }, [data])

  useEffect(() => {
    if (
      dataCertificate?.orders.creditCounselingCertificateExistsByCaseId
        .creditCounselingCertificateExists
    ) {
      setCertificateExists(true)
    }
  }, [dataCertificate])

  const disableAddDocuments = () => {
    if (pendingOrAcceptedDocumentsCount(documents) > 0 || certificateExists) {
      if (documentProcessing.selectedDocumentCategory?.name) {
        if (
          documentProcessing.selectedDocumentCategory.name ===
          'Certificate of Credit Counseling'
        ) {
          return true
        }
      }
    }
    return false
  }

  const handleShowDocumentsAddReplaceModal = () => {
    setShowDocumentsAddReplaceModal(true)
  }

  const handleCloseDocumentsAddReplaceModal = () => {
    setShowDocumentsAddReplaceModal(false)
  }

  const renderDocument = useCallback(
    (document: Document, index: number) => {
      if (document.status !== 'Replaced') {
        return (
          <DocumentsFolderListItem
            key={document.id}
            onClick={() => {
              dispatch(setSelectedDocumentId(document.id))
              dispatch(setSelectedDocument(document))
              dispatch(setSelectedDocumentIndex(index))
              history.push('/dashboard/documents/detail')
            }}
          >
            <DocumentsFolderListItemIcon>
              <span className='mdi mdi-file-pdf' />
            </DocumentsFolderListItemIcon>
            <DocumentsFolderListItemContent>
              <p>{document.name}</p>
              <span>Uploaded: {formatDate(document.uploaded)}</span>
              <span className={getStatus(document.status)}>
                {document.status}
              </span>
            </DocumentsFolderListItemContent>
            <span className='mdi mdi-chevron-right' />
          </DocumentsFolderListItem>
        )
      }

      return null
    },
    [document]
  )

  const renderCertificate = useCallback(() => {
    return (
      <>
        {documents.length === 0 ? (
          <DocumentsFolderCertificateContainer>
            <p>
              You can submit your certificate here if you already completed the
              pre-filing course.
            </p>
            {!certificateExists ? (
              <ContentNotAvailable
                iconClass='mdi-file-multiple-outline light-icon'
                datatestid='documents-subcategory-no-submitted-cert'
              >
                You haven't submitted any certificate. Select Add Documents to
                submit your certificate.
              </ContentNotAvailable>
            ) : (
              <ContentNotAvailable
                iconClass='mdi-file-check-outline'
                datatestid='documents-subcategory-completed-cert'
              >
                You have completed the first course. Your certificate has been
                sent to your attorney.
              </ContentNotAvailable>
            )}
          </DocumentsFolderCertificateContainer>
        ) : (
          <>
            {documents[0].status === 'Pending' ? (
              <p>
                You can submit your certificate here if you already completed
                the pre-filing course.
              </p>
            ) : null}
            {documents[0].status === 'Accepted' ? (
              <p>
                You have completed the first course. Your certificate has been
                sent to your attorney.
              </p>
            ) : null}
            <DocumentsFolderList>
              {documents.map((document, i) => renderDocument(document, i))}
            </DocumentsFolderList>
          </>
        )}
      </>
    )
  }, [documents])

  const renderDocumentList = () => {
    if (!isCertificate) {
      return (
        <>
          {documents.length === 0 ? (
            <DocumentListEmptyContainer data-testid='documents-subcategory-empty-list'>
              <ContentNotAvailable
                iconClass='mdi-file-multiple-outline light-icon'
                datatestid='documents-subcategory-empty-list-content'
              >
                You haven't submitted {''}
                {documentProcessing.selectedDocumentCategory?.name?.toLocaleLowerCase()}
              </ContentNotAvailable>
            </DocumentListEmptyContainer>
          ) : (
            <DocumentsFolderList data-testid='documents-subcategory-list'>
              {documents.map((document, i) => renderDocument(document, i))}
            </DocumentsFolderList>
          )}
        </>
      )
    }

    return renderCertificate()
  }

  const loadings = loading || loadingCertificate

  const loadingDelay = useLoadingDelay(loadings)

  if (error || errorCertificate) {
    return <div>{error?.message ?? errorCertificate?.message}</div>
  }

  return (
    <>
      <GoBackHeader text={goBackHeaderName} />
      {loadingDelay && loadingSpinnerPortal()}
      <DocumentsFolderContainer>
        <h2 data-testid='documents-subcategory-title'>
          {documentProcessing.selectedDocumentCategory?.name}
        </h2>
        {renderDocumentList()}
        <DocumentsFolderBtnContainer>
          <Button
            data-testid='documents-subcategory-add-documents-button'
            disabled={disableAddDocuments()}
            type='button'
            onClick={handleShowDocumentsAddReplaceModal}
          >
            Add Documents
          </Button>
        </DocumentsFolderBtnContainer>
        <DocumentsAddReplaceModal
          businessObjectId={businessObjectId}
          businessObjectType={businessObjectType}
          replacedDocumentId={null}
          show={showDocumentsAddReplaceModal}
          title='Add Documents'
          onHide={handleCloseDocumentsAddReplaceModal}
          folderName={documentProcessing?.selectedDocumentCategory?.name ?? ''}
          folderDescription={
            documentProcessing?.selectedDocumentCategory?.description ?? ''
          }
          isAiCase={false}
        />
      </DocumentsFolderContainer>
    </>
  )
}

export default DocumentsChildFolder
